import React, { useState, useEffect } from 'react';
import { Globe, MapPin } from 'lucide-react';
import { ImagePlus, Eye } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, User, Mail, Phone, Save, Home, IdCard, Users, ShieldCheck, DollarSign, Tag } from 'lucide-react';
import { getApiUrl } from '../utils/apiConfig';

const MemberFormModal = ({ isOpen, onClose, onSave, memberToEdit }) => {
  const [formData, setFormData] = useState({
  name: '',
  email: '',
  password: '',
  phone: '',
  address: '',
  birthDate: '',
  membershipType: '',
  status: 'active',
  joinDate: new Date().toISOString().slice(0, 10),
  role: 'user',
  photoUrl: '',
  photoFile: null,
  pais_nacimiento: '',
  direccion_nacimiento: '',
  rut: '',
  familia_adultos: '',
  familia_ninos: ''
  });
  
  const [membershipSuggestion, setMembershipSuggestion] = useState(null);
  const [membershipTypes, setMembershipTypes] = useState([]);

  // Cargar tipos de membresía disponibles
  useEffect(() => {
    fetch(getApiUrl('/api/tipos-membresia/activos'))
      .then(res => res.json())
      .then(data => setMembershipTypes(Array.isArray(data) ? data : []))
      .catch(err => console.error('Error al cargar tipos de membresía:', err));
  }, []);

  useEffect(() => {
    if (memberToEdit) {
      // Normalizar valores null/undefined a strings vacíos
      const normalizedData = {};
      Object.entries(memberToEdit).forEach(([key, value]) => {
        normalizedData[key] = (value === null || value === undefined) ? '' : value;
      });
      
      setFormData({
        ...normalizedData,
        password: '', // Por seguridad, no mostrar la contraseña
        photoUrl: memberToEdit.photoUrl || '',
        photoFile: null
      });
    } else {
      setFormData({
        name: '',
        email: '',
        password: '',
        phone: '',
        address: '',
        birthDate: '',
        membershipType: '',
        status: 'active',
        joinDate: new Date().toISOString().slice(0, 10),
        role: 'user',
        photoUrl: '',
        photoFile: null,
        pais_nacimiento: '',
        direccion_nacimiento: '',
        rut: '',
        familia_adultos: '',
        familia_ninos: ''
      });
    }
  }, [memberToEdit]);

  // Auto-cálculo de membresía basado en familia
  useEffect(() => {
    const adultos = parseInt(formData.familia_adultos) || 0;
    const ninos = parseInt(formData.familia_ninos) || 0;
    const totalFamilia = adultos + ninos;

    if (totalFamilia > 0) {
      fetch(getApiUrl(`/api/tipos-membresia/calcular/${totalFamilia}`))
        .then(res => res.json())
        .then(data => {
          if (!data.error) {
            setMembershipSuggestion(data);
            // Actualizar automáticamente el campo membershipType
            setFormData(prev => ({
              ...prev,
              membershipType: data.nombre
            }));
          }
        })
        .catch(err => console.error('Error al calcular membresía:', err));
    } else {
      setMembershipSuggestion(null);
    }
  }, [formData.familia_adultos, formData.familia_ninos]);

  const handlePhotoChange = (e) => {
  const file = e.target.files[0];
  if (file) {
    setFormData(prev => ({
      ...prev,
      photoFile: file
    }));
  }
};

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

const handleSubmit = (e) => {
  e.preventDefault();

  const dataToSend = new FormData();
  
  Object.entries(formData).forEach(([key, value]) => {
    if (key === 'photoFile' && value) {
      dataToSend.append('photo', value);
    } else if (key !== 'photoFile') {
      // Filtrar valores null, undefined, o strings "null"/"undefined"
      if (value !== null && value !== undefined && value !== 'null' && value !== 'undefined') {
        dataToSend.append(key, value === '' ? '' : value);
      } else if (key === 'name' || key === 'email' || key === 'id') {
        // Campos críticos siempre deben enviarse
        dataToSend.append(key, value || '');
      }
    }
  });
  
  // Pasar los datos al componente padre (Members.js) para que haga la llamada
  onSave(dataToSend);
  onClose();
};

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="bg-white rounded-3xl p-8 shadow-2xl w-full max-w-2xl relative"
            initial={{ scale: 0.9, y: 50 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 50 }}
            transition={{ type: "spring", damping: 20, stiffness: 300 }}
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors duration-200"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {memberToEdit ? 'Editar Socio' : 'Registrar Nuevo Socio'}
            </h2>


            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Grupo 1: Datos del Socio */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
                <div className="flex items-center mb-3">
                  <Users className="w-6 h-6 text-blue-500 mr-2" />
                  <h3 className="text-lg font-bold text-blue-700">Datos del Socio</h3>
                </div>
              {/* Fila 1: Nombre, Correo, Rut */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nombre</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Nombre del socio" className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all duration-300" required />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Correo electrónico</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="Correo electrónico" className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all duration-300" required />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">RUT</label>
                  <input type="text" name="rut" value={formData.rut} onChange={handleChange} placeholder="RUT" className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl" />
                </div>
              </div>

              {/* Fila 2: Fecha de nacimiento, Teléfono, Dirección */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Fecha de nacimiento</label>
                  <input type="date" name="birthDate" value={formData.birthDate || ''} onChange={handleChange} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input type="text" name="phone" value={formData.phone} onChange={handleChange} placeholder="Teléfono" className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Dirección</label>
                  <div className="relative">
                    <Home className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input type="text" name="address" value={formData.address} onChange={handleChange} placeholder="Dirección" className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl" />
                  </div>
                </div>
              </div>
<div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-2">
                <div className="md:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">País nacimiento</label>
                  <div className="relative flex items-center">
                    <input type="text" name="pais_nacimiento" value={formData.pais_nacimiento} onChange={handleChange} placeholder="País de nacimiento" className="w-full pl-10 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm" />
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-6 h-6 text-gray-400" />
                  </div>
                </div>
                <div className="md:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Dirección nacimiento</label>
                  <div className="relative flex items-center">
                    <input type="text" name="direccion_nacimiento" value={formData.direccion_nacimiento} onChange={handleChange} placeholder="Dirección de nacimiento" className="w-full pl-10 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm" />
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-6 h-6 text-gray-400" />
                  </div>
                </div>
                <div className="flex flex-col justify-end">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Cantidad Familia</label>
                  <div className="flex gap-2">
                    <div className="relative group flex items-center">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-6 h-6 text-gray-400" />
                      <input type="number" name="familia_adultos" value={formData.familia_adultos} onChange={handleChange} min="0" className="w-20 pl-10 py-3 bg-gray-50 border border-gray-200 rounded-xl text-xs" placeholder="Adultos" />
                      <span className="absolute left-1/2 -translate-x-1/2 top-8 bg-gray-800 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">Adultos</span>
                    </div>
                    <div className="relative group flex items-center">
                      <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-6 h-6 text-gray-400" />
                      <input type="number" name="familia_ninos" value={formData.familia_ninos} onChange={handleChange} min="0" className="w-20 pl-10 py-3 bg-gray-50 border border-gray-200 rounded-xl text-xs" placeholder="Niños" />
                      <span className="absolute left-1/2 -translate-x-1/2 top-8 bg-gray-800 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">Niños</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Sugerencia de membresía */}
              {membershipSuggestion && (
                <motion.div 
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-2 p-2 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-300 rounded-lg"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Tag className="w-4 h-4 text-green-600" />
                      <div>
                        <p className="text-xs font-semibold text-green-700">{membershipSuggestion.nombre}</p>
                        <p className="text-[10px] text-gray-500">{membershipSuggestion.descripcion}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 bg-white px-2 py-1 rounded shadow-sm">
                      <DollarSign className="w-3 h-3 text-green-600" />
                      <span className="text-sm font-bold text-green-600">
                        ${parseFloat(membershipSuggestion.monto_anual).toLocaleString('es-CL')}
                      </span>
                    </div>
                  </div>
                </motion.div>
              )}
              
              {/* Fila país y dirección de nacimiento */}
              {/* Fila país/dirección de nacimiento y cantidades en familia */}
              </div>
              {/* Título grupo 2 */}
              

              {/* El resto de campos como estaban */}

              {/* Sección datos de membresía y otros */}
              {/* Fila 5: Tipo de membresía, Fecha de ingreso y Estado */}
              {/* Título grupo 3 */}
              {/* Grupo 2: Datos de Membresía */}
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-4">
                <div className="flex items-center mb-3">
                  <ShieldCheck className="w-6 h-6 text-purple-500 mr-2" />
                  <h3 className="text-lg font-bold text-purple-700">Datos de Membresía</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-2">
                {/* Tipo de Membresía */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Membresía</label>
                  <select name="membershipType" value={formData.membershipType} onChange={handleChange} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl" required>
                    <option value="">Seleccione un tipo</option>
                    {membershipTypes.map(type => (
                      <option key={type.id} value={type.nombre}>
                        {type.nombre} - ${parseFloat(type.monto_anual).toLocaleString('es-CL')}/año
                      </option>
                    ))}
                  </select>
                </div>
                {/* Fecha de Ingreso */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Fecha de Ingreso</label>
                  <input type="date" name="joinDate" value={formData.joinDate || ''} onChange={handleChange} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl" required />
                </div>
                {/* Estado */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Estado</label>
                  <select name="status" value={formData.status} onChange={handleChange} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl" required>
                    <option value="active">Activo</option>
                    <option value="expired">Expirado</option>
                    <option value="pending">Pendiente</option>
                  </select>
                </div>
              </div>
              </div>
            

              

              {/* Botón Guardar (ocupa las 2 columnas en pantallas grandes) */}
              {/* Título grupo 3 */}
              {/* Grupo 3: Datos de Identificación y Acceso */}
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4">
                <div className="flex items-center mb-3">
                  <IdCard className="w-6 h-6 text-green-500 mr-2" />
                  <h3 className="text-lg font-bold text-green-700">Datos de Identificación y Acceso</h3>
                </div>
              {/* Fila 6: Rol, Contraseña y Foto */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-2">
                {/* Rol */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Rol</label>
                  <select name="role" value={formData.role} onChange={handleChange} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl" required>
                    <option value="user">Usuario</option>
                    <option value="admin">Administrador</option>
                  </select>
                </div>
                {/* Contraseña */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Contraseña</label>
                  <input type="password" name="password" value={formData.password} onChange={handleChange} placeholder={memberToEdit ? "Dejar en blanco para no cambiar" : "Contraseña"} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl" autoComplete="new-password" required={!memberToEdit} />
                </div>
                {/* Foto */}
                <div className="flex items-center gap-4">
                  {/* Icono para subir foto con título arriba y texto debajo al pasar el mouse */}
                  <div className="relative group flex flex-col items-center">
                    <span className="font-semibold text-black mb-1">Foto</span>
                    <label htmlFor="photo-upload" className="cursor-pointer flex flex-col items-center justify-center">
                      <ImagePlus className="w-8 h-8 text-blue-500 hover:text-blue-700" />
                      <span className="mt-2 text-xs text-blue-600 font-medium opacity-0 group-hover:opacity-100 transition-opacity">Sube tu foto</span>
                    </label>
                    <input id="photo-upload" type="file" accept="image/*" onChange={handlePhotoChange} className="hidden" />
                  </div>
                  {/* Previsualización al costado */}
                  {formData.photoFile && (
                    <div className="relative group flex flex-col items-center">
                      <img src={URL.createObjectURL(formData.photoFile)} alt="Vista previa" className="w-16 h-16 object-cover rounded-full border shadow" />
                      {/* Tooltip aún más arriba, centrado y sin tapar la previsualización */}
                      <div className="absolute left-1/2 -translate-x-1/2 -top-72 z-10 bg-white border border-gray-300 rounded shadow-lg p-2 opacity-0 group-hover:opacity-100 transition-opacity" style={{ pointerEvents: 'none' }}>
                        <img src={URL.createObjectURL(formData.photoFile)} alt="Tamaño real" style={{ maxWidth: '300px', maxHeight: '300px' }} />
                        <div className="text-xs text-gray-600 text-center mt-1">Vista tamaño real</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              </div>
              

              {/* Botón Guardar (ocupa las 2 columnas en pantallas grandes) */}
              <div className="md:col-span-2">
                <motion.button
                  type="submit"
                  className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Save className="w-5 h-5" />
                  Guardar Socio
                </motion.button>
              </div>
             
            </form>
          </motion.div>
        </motion.div>
      )}
                {/* Fila país/dirección de nacimiento y cantidades en familia */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-2">
                  <div className="md:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">País nacimiento</label>
                    <input type="text" name="pais_nacimiento" value={formData.pais_nacimiento} onChange={handleChange} placeholder="País de nacimiento" className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm" />
                  </div>
                  <div className="md:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Dirección nacimiento</label>
                    <input type="text" name="direccion_nacimiento" value={formData.direccion_nacimiento} onChange={handleChange} placeholder="Dirección de nacimiento" className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm" />
                  </div>
                  <div className="flex flex-col justify-end">
                    <label className="block text-xs font-medium text-gray-700 mb-1">Cantidad de Familia</label>
                    <div className="flex gap-2">
                  <input type="number" name="familia_adultos" value={formData.familia_adultos} onChange={handleChange} min="0" className="w-10 px-1 py-1 bg-gray-50 border border-gray-200 rounded-xl text-xs" placeholder="Adultos" />
                  <input type="number" name="familia_ninos" value={formData.familia_ninos} onChange={handleChange} min="0" className="w-10 px-1 py-1 bg-gray-50 border border-gray-200 rounded-xl text-xs" placeholder="Niños" />
                    </div>
                  </div>
                </div>
    </AnimatePresence>
  );
};

export default MemberFormModal;